/**
 * Cart Order Bump Component
 * Manages a Flickity carousel for recommended products in the cart drawer
 */
class cartOrderBump extends HTMLElement {
  /** @type {Object|null} */
  #flickityInstance = null;

  /** @type {Object} */
  static #FLICKITY_CONFIG = {
    cellAlign: 'left',
    contain: true,
    pageDots: false,
    prevNextButtons: true,
    wrapAround: false
  };

  /** @type {string} */
  static #CAROUSEL_SELECTOR = '.main-carousel';
  
  /** @type {string} */
  static #CONTAINER_SELECTOR = '#cart-order-bump';
  
  /** @type {string} */
  static #ITEM_SELECTOR = '.cart-order-bump__item';

  constructor() {
    super();
    this.#setupEventListeners();
  }

  connectedCallback() {
    if (this.#isVisible()) {
      setTimeout(() => this.initCarousel(), 100);
    }
    // If not visible, will initialize when cart drawer opens via dialog:open event
  }

  /**
   * Sets up event listeners for cart drawer opening
   */
  #setupEventListeners() {
    // Primary approach: Listen for dialog open events
    document.addEventListener('dialog:open', (event) => {
      // Check if the opened dialog contains our cart-order-bump element
      if (event.target instanceof HTMLElement) {
        const hasOrderBump = event.target.querySelector('cart-order-bump') !== null;
        
        if (hasOrderBump) {
          setTimeout(() => this.initCarousel(), 150);
        }
      }
    });

    // Fallback approach: Listen for cart updates (when drawer might already be open)
    document.addEventListener('cart:update', () => {
      // Small delay to allow DOM to update
      setTimeout(() => {
        if (this.#isVisible()) {
          this.initCarousel();
        }
      }, 100);
    });

    // Additional fallback: Check periodically if element becomes visible
    const checkVisibility = () => {
      const carouselElement = document.querySelector(cartOrderBump.#CAROUSEL_SELECTOR);
      if (this.#isVisible() && carouselElement && !this.#isFlickityWorking(carouselElement)) {
        this.initCarousel();
      }
    };

    // Check every 2 seconds for the first 10 seconds after element creation
    let checkCount = 0;
    const intervalId = setInterval(() => {
      checkVisibility();
      checkCount++;
      if (checkCount >= 5) { // Stop after 10 seconds
        clearInterval(intervalId);
      }
    }, 2000);
  }

  /**
   * Initializes or reinitializes the Flickity carousel
   * Called automatically when cart drawer opens or by connectedCallback when already visible
   */
  initCarousel() {
    const carouselElement = this.#getCarouselElement();
    if (!carouselElement) {
      return;
    }

    const items = carouselElement.querySelectorAll(cartOrderBump.#ITEM_SELECTOR);
    if (items.length === 0) return;

    if (this.#shouldRecreateFlickity(carouselElement)) {
      this.#destroyExistingFlickity(carouselElement);
    } else if (this.#isFlickityWorking(carouselElement)) {
      return; // Already working properly
    }

    if (this.#isVisible()) {
      this.#createFlickityInstance(carouselElement);
    }
  }

  /**
   * Gets the carousel DOM element
   * @returns {Element|null}
   */
  #getCarouselElement() {
    const element = document.querySelector(cartOrderBump.#CAROUSEL_SELECTOR);
    return element?.parentNode ? element : null;
  }

  /**
   * Checks if the order bump container is visible in the viewport
   * @returns {boolean}
   */
  #isVisible() {
    const container = document.querySelector(cartOrderBump.#CONTAINER_SELECTOR);
    if (!container) return false;

    const rect = container.getBoundingClientRect();
    const style = getComputedStyle(container);
    
    return (
      rect.height > 0 &&
      rect.width > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      style.opacity !== '0'
    );
  }

  /**
   * Checks if Flickity is already working properly
   * @param {Element} element 
   * @returns {boolean}
   */
  #isFlickityWorking(element) {
    if (!element.classList.contains('flickity-enabled')) return false;
    
    const viewport = element.querySelector('.flickity-viewport');
    return viewport instanceof HTMLElement && viewport.offsetHeight > 0;
  }

  /**
   * Determines if Flickity needs to be recreated
   * @param {Element} element 
   * @returns {boolean}
   */
  #shouldRecreateFlickity(element) {
    return element.classList.contains('flickity-enabled') && !this.#isFlickityWorking(element);
  }

  /**
   * Safely destroys existing Flickity instance
   * @param {Element} element 
   */
  #destroyExistingFlickity(element) {
    try {
      // @ts-ignore - Flickity is loaded globally
      const existingInstance = Flickity.data(element);
      if (existingInstance) {
        existingInstance.destroy();
      }
    } catch (error) {
      // Fallback: manually remove classes if destroy fails
      element.classList.remove('flickity-enabled', 'flickity-rtl');
    }
  }

  /**
   * Creates a new Flickity instance
   * @param {Element} element 
   */
  #createFlickityInstance(element) {
    try {
      const items = element.querySelectorAll(cartOrderBump.#ITEM_SELECTOR);
      
      // Add data attribute for CSS styling
      const container = document.querySelector(cartOrderBump.#CONTAINER_SELECTOR);
      if (container) {
        container.setAttribute('data-item-count', items.length.toString());
      }
      
      // Create config with conditional arrows
      const config = {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: items.length > 1, // Hide arrows when there's only 1 item
        wrapAround: false
      };
      
      // @ts-ignore - Flickity is loaded globally
      this.#flickityInstance = new Flickity(element, config);
    } catch (error) {
      // Silently fail - Flickity may not be loaded yet
    }
  }
}

customElements.define('cart-order-bump', cartOrderBump);